import { useState } from "react";
import { Box, Typography, Chip, Paper, IconButton, Select, MenuItem, Popover, Badge, Button } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { PersonRemove, FilterList } from "@mui/icons-material";
import { DragDropContext, Droppable } from "@hello-pangea/dnd";
import type { DealConfigurationProps } from "./DealConfiguration.types";
import { useDealConfigurationViewModel, DEAL_PROPERTIES, type DealSortOption } from "./DealConfiguration.vm";
import {
	GrowerCard,
	JobCard,
	EntityCardMenu,
	UnassignedGrowersPanel,
	EntitySearchInput,
	makeGrowerDraggableId,
	makeJobDraggableId,
} from "../shared";
import { PortalTooltip } from "../../../../../../helpers";

function DealPropertyChip({
	label,
	value,
	onClick,
	isFilterActive,
	isHighlighted,
	onMouseEnter,
	onMouseLeave,
}: {
	label: string;
	value: string;
	onClick?: () => void;
	isFilterActive?: boolean;
	isHighlighted?: boolean;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
}) {
	return (
		<Chip
			label={
				label ? (
					<>
						<span style={{ fontWeight: 700 }}>{label}:</span> {value}
					</>
				) : (
					value
				)
			}
			size="small"
			onClick={onClick}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			sx={{
				bgcolor: isHighlighted ? "#E3F2FD" : "transparent",
				color: "text.primary",
				fontWeight: 400,
				fontSize: "0.7rem",
				height: 24,
				cursor: "pointer",
				outline: isFilterActive ? "2px solid" : "none",
				outlineColor: "primary.main",
				transition: "background-color 0.15s",
			}}
		/>
	);
}

function DealsHeader({
	vm,
	totalCount,
	shownCount,
}: {
	vm: ReturnType<typeof useDealConfigurationViewModel>;
	totalCount: number;
	shownCount: number;
}) {
	const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
	const isFiltering = vm.activeDealFilterCount > 0 || vm.searchText.trim().length > 0;

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
			<Typography variant="subtitle2" sx={{ whiteSpace: "nowrap" }}>
				Configurations {isFiltering ? `(${shownCount} / ${totalCount})` : `(${totalCount})`}
			</Typography>
			<EntitySearchInput
				value={vm.searchText}
				onChange={vm.setSearchText}
				placeholder="Search configs..."
				sx={{ ml: "auto" }}
			/>
			<Select
				size="small"
				value={vm.dealSortOption}
				onChange={(e: SelectChangeEvent) => vm.setDealSortOption(e.target.value as DealSortOption)}
				sx={{
					fontSize: "0.75rem",
					height: 28,
					minWidth: 0,
					"& .MuiSelect-select": { py: 0.25, pr: "24px !important", pl: 1 },
				}}
			>
				<MenuItem value="default" sx={{ fontSize: "0.8rem" }}>
					Default
				</MenuItem>
				<MenuItem value="density-asc" sx={{ fontSize: "0.8rem" }}>
					Density ↑
				</MenuItem>
				<MenuItem value="density-desc" sx={{ fontSize: "0.8rem" }}>
					Density ↓
				</MenuItem>
				<MenuItem value="depth-asc" sx={{ fontSize: "0.8rem" }}>
					Depth ↑
				</MenuItem>
				<MenuItem value="depth-desc" sx={{ fontSize: "0.8rem" }}>
					Depth ↓
				</MenuItem>
				<MenuItem value="growers-desc" sx={{ fontSize: "0.8rem" }}>
					Growers ↑
				</MenuItem>
				<MenuItem value="growers-asc" sx={{ fontSize: "0.8rem" }}>
					Growers ↓
				</MenuItem>
			</Select>
			<PortalTooltip title="Filter configurations">
				<IconButton size="small" onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
					<Badge badgeContent={vm.activeDealFilterCount} color="primary" overlap="circular">
						<FilterList fontSize="small" />
					</Badge>
				</IconButton>
			</PortalTooltip>
			<PortalTooltip title="Unassign all from all configurations">
				<IconButton size="small" onClick={vm.unassignAll}>
					<PersonRemove fontSize="small" />
				</IconButton>
			</PortalTooltip>
			<Popover
				open={Boolean(filterAnchorEl)}
				anchorEl={filterAnchorEl}
				onClose={() => setFilterAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<Box sx={{ p: 1, maxHeight: 320, overflowY: "auto" }}>
					{DEAL_PROPERTIES.map((prop) => {
						const options = vm.dealFilterOptions[prop.name] || [];
						if (options.length <= 1) return null;
						const activeSet = vm.dealFilters[prop.name] || new Set<string>();
						return (
							<Box key={prop.name} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5, flexWrap: "wrap" }}>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ fontWeight: 600, fontSize: "0.65rem", minWidth: 48 }}
								>
									{prop.label}:
								</Typography>
								{options.map((value) => {
									const isActive = activeSet.has(value);
									return (
										<Chip
											key={value}
											label={value}
											size="small"
											variant={isActive ? "filled" : "outlined"}
											color={isActive ? "primary" : "default"}
											onClick={() => {
												const next = new Set(activeSet);
												if (isActive) {
													next.delete(value);
												} else {
													next.add(value);
												}
												vm.setDealFilter(prop.name, next);
											}}
											sx={{ fontSize: "0.65rem", height: 22, cursor: "pointer" }}
										/>
									);
								})}
							</Box>
						);
					})}
					{isFiltering && (
						<Button
							size="small"
							onClick={() => {
								vm.clearDealFilters();
								setFilterAnchorEl(null);
							}}
							sx={{ mt: 1, width: "100%" }}
						>
							Clear All
						</Button>
					)}
				</Box>
			</Popover>
		</Box>
	);
}

export function DealConfiguration(props: DealConfigurationProps) {
	const vm = useDealConfigurationViewModel(props);

	return (
		<DragDropContext onBeforeCapture={vm.handleBeforeCapture} onDragEnd={vm.handleDragEnd}>
			<Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
				<Box sx={{ mb: 2 }}>
					<Typography variant="body2" color="text.secondary">
						Drag-and-drop growers (or individual jobs) to assign them to a sampling configuration.
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Hover a property to highlight matching values. Click to filter.
					</Typography>
				</Box>
				<Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 400 }}>
					<UnassignedGrowersPanel
						unassignedGrowers={vm.unassignedGrowers}
						unassignedJobs={vm.unassignedJobs}
						growerSortOption={vm.growerSortOption}
						setGrowerSortOption={vm.setGrowerSortOption}
						selectedItems={vm.selectedItems}
						clearSelection={vm.clearSelection}
						handleItemClick={vm.handleItemClick}
						draggingSelectedCount={vm.draggingSelectedCount}
						UNASSIGNED_DROPPABLE_ID={vm.UNASSIGNED_DROPPABLE_ID}
						onExplodeGrower={vm.onExplodeGrower}
						onRegroupGrower={vm.onRegroupGrower}
					/>

					{/* Right: Deal cards grid */}
					<Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
						<DealsHeader vm={vm} totalCount={vm.totalDealCount} shownCount={vm.filteredSortedDeals.length} />
						<Box sx={{ flex: 1, overflowY: "auto" }}>
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
									gap: 1.5,
								}}
							>
								{vm.filteredSortedDeals.map((deal) => {
									const assignedGrowers = vm.growersByEntity.get(deal.id) || [];
									const assignedJobs = vm.jobsByEntity.get(deal.id) || [];
									const toggleFilter = (propertyName: string, value: string) => {
										const current = vm.dealFilters[propertyName] || new Set<string>();
										const next = new Set(current);
										if (next.has(value)) next.delete(value);
										else next.add(value);
										vm.setDealFilter(propertyName, next);
									};
									const isActive = (propertyName: string, value: string) =>
										vm.dealFilters[propertyName]?.has(value) ?? false;
									const isHovered = (propertyName: string, value: string) =>
										vm.hoveredChip?.propertyName === propertyName && vm.hoveredChip?.value === value;
									return (
										<Droppable key={deal.id} droppableId={deal.id}>
											{(provided, snapshot) => (
												<Paper
													ref={provided.innerRef}
													{...provided.droppableProps}
													variant="outlined"
													sx={{
														p: 1.5,
														bgcolor: snapshot.isDraggingOver ? "primary.50" : "background.paper",
														borderColor: snapshot.isDraggingOver ? "primary.main" : "divider",
														borderWidth: snapshot.isDraggingOver ? 2 : 1,
														transition: "border-color 0.15s, background-color 0.15s",
													}}
												>
													{/* Deal properties — 2-column grid */}
													<Box sx={{ mb: 1, display: "flex", alignItems: "flex-start" }}>
														<Box sx={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
															<DealPropertyChip
																label="Density"
																value={`${deal.density} acre`}
																onClick={() => toggleFilter("density", `${deal.density} acre`)}
																isFilterActive={isActive("density", `${deal.density} acre`)}
																isHighlighted={isHovered("density", `${deal.density} acre`)}
																onMouseEnter={() =>
																	vm.setHoveredChip({ propertyName: "density", value: `${deal.density} acre` })
																}
																onMouseLeave={() => vm.setHoveredChip(null)}
															/>
															<DealPropertyChip
																label="Depth"
																value={`${deal.depth} in`}
																onClick={() => toggleFilter("depth", `${deal.depth} in`)}
																isFilterActive={isActive("depth", `${deal.depth} in`)}
																isHighlighted={isHovered("depth", `${deal.depth} in`)}
																onMouseEnter={() =>
																	vm.setHoveredChip({ propertyName: "depth", value: `${deal.depth} in` })
																}
																onMouseLeave={() => vm.setHoveredChip(null)}
															/>
															<DealPropertyChip
																label="Type"
																value={deal.type}
																onClick={() => toggleFilter("type", deal.type)}
																isFilterActive={isActive("type", deal.type)}
																isHighlighted={isHovered("type", deal.type)}
																onMouseEnter={() => vm.setHoveredChip({ propertyName: "type", value: deal.type })}
																onMouseLeave={() => vm.setHoveredChip(null)}
															/>
															{(deal.isComposite || deal.isMultiComposite) && (
																<DealPropertyChip
																	label=""
																	value={deal.isMultiComposite ? "Multi-Composite" : "Composite"}
																	onClick={() =>
																		toggleFilter("composite", deal.isMultiComposite ? "Multi-Composite" : "Composite")
																	}
																	isFilterActive={isActive(
																		"composite",
																		deal.isMultiComposite ? "Multi-Composite" : "Composite",
																	)}
																	isHighlighted={isHovered(
																		"composite",
																		deal.isMultiComposite ? "Multi-Composite" : "Composite",
																	)}
																	onMouseEnter={() =>
																		vm.setHoveredChip({
																			propertyName: "composite",
																			value: deal.isMultiComposite ? "Multi-Composite" : "Composite",
																		})
																	}
																	onMouseLeave={() => vm.setHoveredChip(null)}
																/>
															)}
															<DealPropertyChip
																label="Package"
																value={deal.testPackage || "None"}
																onClick={() => toggleFilter("testPackage", deal.testPackage || "None")}
																isFilterActive={isActive("testPackage", deal.testPackage || "None")}
																isHighlighted={isHovered("testPackage", deal.testPackage || "None")}
																onMouseEnter={() =>
																	vm.setHoveredChip({ propertyName: "testPackage", value: deal.testPackage || "None" })
																}
																onMouseLeave={() => vm.setHoveredChip(null)}
															/>
															<DealPropertyChip
																label="Season"
																value={deal.season}
																onClick={() => toggleFilter("season", deal.season)}
																isFilterActive={isActive("season", deal.season)}
																isHighlighted={isHovered("season", deal.season)}
																onMouseEnter={() => vm.setHoveredChip({ propertyName: "season", value: deal.season })}
																onMouseLeave={() => vm.setHoveredChip(null)}
															/>
															<DealPropertyChip
																label="Micros"
																value={`${deal.frequencyOfMicros ?? 0}`}
																onClick={() => toggleFilter("frequencyOfMicros", `${deal.frequencyOfMicros ?? 0}`)}
																isFilterActive={isActive("frequencyOfMicros", `${deal.frequencyOfMicros ?? 0}`)}
																isHighlighted={isHovered("frequencyOfMicros", `${deal.frequencyOfMicros ?? 0}`)}
																onMouseEnter={() =>
																	vm.setHoveredChip({
																		propertyName: "frequencyOfMicros",
																		value: `${deal.frequencyOfMicros ?? 0}`,
																	})
																}
																onMouseLeave={() => vm.setHoveredChip(null)}
															/>
															{deal.labName && (
																<DealPropertyChip
																	label="Lab"
																	value={deal.labName}
																	onClick={() => toggleFilter("labName", deal.labName)}
																	isFilterActive={isActive("labName", deal.labName)}
																	isHighlighted={isHovered("labName", deal.labName)}
																	onMouseEnter={() =>
																		vm.setHoveredChip({ propertyName: "labName", value: deal.labName })
																	}
																	onMouseLeave={() => vm.setHoveredChip(null)}
																/>
															)}
															{deal.specialTag && (
																<DealPropertyChip
																	label="Tag"
																	value={deal.specialTag}
																	onClick={() => toggleFilter("specialTag", deal.specialTag!)}
																	isFilterActive={isActive("specialTag", deal.specialTag!)}
																	isHighlighted={isHovered("specialTag", deal.specialTag!)}
																	onMouseEnter={() =>
																		vm.setHoveredChip({ propertyName: "specialTag", value: deal.specialTag! })
																	}
																	onMouseLeave={() => vm.setHoveredChip(null)}
																/>
															)}
														</Box>
														<EntityCardMenu
															entityId={deal.id}
															onAssignAll={vm.assignAllToEntity}
															onAssignUnassigned={vm.assignUnassignedToEntity}
															onUnassignAll={vm.unassignAllFromEntity}
															selectedCount={vm.selectedItems.size}
															onAssignSelected={vm.assignSelectedToEntity}
														/>
													</Box>

													{/* Assigned growers & jobs */}
													<Box sx={{ minHeight: 36 }}>
														{assignedGrowers.length === 0 && assignedJobs.length === 0 && !snapshot.isDraggingOver && (
															<Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
																Drop growers/jobs here
															</Typography>
														)}
														{assignedGrowers.map((grower, index) => (
															<GrowerCard
																key={makeGrowerDraggableId(grower.name)}
																grower={grower}
																index={index}
																isSelected={vm.selectedItems.has(makeGrowerDraggableId(grower.name))}
																onToggleSelect={vm.handleItemClick}
																multiDragCount={
																	vm.selectedItems.has(makeGrowerDraggableId(grower.name))
																		? vm.draggingSelectedCount
																		: 0
																}
																onExplode={vm.onExplodeGrower}
															/>
														))}
														{assignedJobs.map((job, idx) => (
															<JobCard
																key={makeJobDraggableId(job.jobId)}
																job={job}
																index={assignedGrowers.length + idx}
																isSelected={vm.selectedItems.has(makeJobDraggableId(job.jobId))}
																onToggleSelect={vm.handleItemClick}
																multiDragCount={
																	vm.selectedItems.has(makeJobDraggableId(job.jobId)) ? vm.draggingSelectedCount : 0
																}
															/>
														))}
														{provided.placeholder}
													</Box>
												</Paper>
											)}
										</Droppable>
									);
								})}
							</Box>
							{vm.filteredSortedDeals.length === 0 && vm.totalDealCount > 0 && (
								<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
									No configurations match the current {vm.searchText.trim() ? "search" : "filters"}.
								</Typography>
							)}
							{vm.totalDealCount === 0 && (
								<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
									No deals available. Please create deals before proceeding.
								</Typography>
							)}
						</Box>
					</Box>
				</Box>
			</Box>
		</DragDropContext>
	);
}
